// src/pages/auth/RegisterPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import PersonalInfoStep from '../../components/auth/registration/PersonalInfoStep';
import AddressStep from '../../components/auth/registration/AddressStep';
import CategoriesStep from '../../components/auth/registration/CategoriesStep';
import BrandsStep from '../../components/auth/registration/BrandsStep';
import VerificationStep from '../../components/auth/registration/VerificationStep';
import ProgressBar from '../../components/common/ProgressBar';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { 
    currentUser, 
    register, 
    logout, // Add logout function
    registrationStep, 
    registrationData, 
    updateRegistrationData,
    nextRegistrationStep,
    previousRegistrationStep
  } = useAuth();
  const { showAlert } = useNotification();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [justRegistered, setJustRegistered] = useState(false);
  
  // If user is already logged in, redirect to dashboard (but not if they just registered)
  useEffect(() => {
    if (currentUser && !justRegistered) {
      navigate('/dashboard');
    }
  }, [currentUser, navigate, justRegistered]);
  
  // Handle final submission
  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setJustRegistered(true);
      await register();
      
      // Log out the user immediately after registration so they have to login
      if (logout) {
        await logout();
      }
      
      showAlert('Registration successful! Please login to continue.', 'success');
      navigate('/login');
    } catch (error) {
      showAlert(error.message || 'Failed to register. Please try again.', 'error');
      setJustRegistered(false);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Determine current step component
  const renderCurrentStep = () => {
    switch (registrationStep) {
      case 1:
        return (
          <PersonalInfoStep 
            data={registrationData}
            updateData={updateRegistrationData}
            nextStep={nextRegistrationStep}
          />
        );
      case 2:
        return (
          <AddressStep 
            data={registrationData}
            updateData={updateRegistrationData}
            nextStep={nextRegistrationStep}
            prevStep={previousRegistrationStep}
          />
        );
      case 3:
        return (
          <CategoriesStep 
            data={registrationData}
            updateData={updateRegistrationData}
            nextStep={nextRegistrationStep}
            prevStep={previousRegistrationStep}
          />
        );
      case 4:
        return (
          <BrandsStep 
            data={registrationData}
            updateData={updateRegistrationData}
            nextStep={nextRegistrationStep}
            prevStep={previousRegistrationStep}
          />
        );
      case 5:
        return (
          <VerificationStep 
            data={registrationData}
            isSubmitting={isSubmitting}
            handleSubmit={handleSubmit}
            prevStep={previousRegistrationStep}
          />
        );
      default:
        return null;
    }
  };

  // Determine if current step needs full width
  const isFullWidthStep = registrationStep === 3 || registrationStep === 4 || registrationStep === 2; // Categories and Brands steps

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-600 to-indigo-900 flex flex-col items-center justify-center py-8 px-4 sm:px-6 lg:px-8">
      <div className={`w-full bg-white rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 ${
        isFullWidthStep 
          ? 'max-w-7xl' // Full width for categories and brands steps
          : 'max-w-4xl'  // Normal width for other steps
      }`}>
        <div className="flex flex-col lg:flex-row">
          {/* Left sidebar with branding and information */}
          <div className={`bg-gradient-to-br from-orange-800 to-indigo-900 text-white p-8 transition-all duration-300 ${
            isFullWidthStep 
              ? 'lg:w-1/4' // Smaller sidebar for categories and brands steps
              : 'lg:w-1/3' // Normal sidebar for other steps
          }`}>
            <div className="h-full flex flex-col">
              <div className="mb-8">
                <h1 className="text-3xl font-bold">ZoneXVendors</h1>
                <p className="text-orange-200 mt-2">Premium Auto Parts Marketplace</p>
              </div>
              
              <div className="flex-grow">
                <h2 className="text-xl font-semibold mb-4">Join Our Platform</h2>
                <p className="text-orange-100 mb-6">
                  Connect with thousands of auto shops and mechanics looking for quality parts
                </p>
                
                <ul className="space-y-3 text-sm">
                  <li className="flex items-center">
                    <svg className="w-5 h-5 mr-2 text-orange-300" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Expand your market reach
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 mr-2 text-orange-300" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Manage inventory efficiently
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 mr-2 text-orange-300" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Real-time analytics dashboard
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 mr-2 text-orange-300" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Secure payment processing
                  </li>
                </ul>
              </div>
              
              <div className="mt-auto pt-6">
                <div className="flex items-center text-sm text-orange-200">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  SSL Secure Registration
                </div>
              </div>
            </div>
          </div>
          
          {/* Right side with registration form */}
          <div className={`p-8 transition-all duration-300 ${
            isFullWidthStep 
              ? 'lg:w-3/4' // Larger content area for categories and brands steps
              : 'lg:w-2/3' // Normal content area for other steps
          }`}>
            <div className={`mx-auto ${
              isFullWidthStep 
                ? 'max-w-none' // No max width constraint for categories and brands steps
                : 'max-w-lg'   // Normal constraint for other steps
            }`}>
              <h2 className="text-2xl font-bold text-gray-900 mb-1">
                Create Your Vendor Account
              </h2>
              <p className="text-gray-600 mb-6">
                Complete the steps below to join our network
              </p>
              
              <ProgressBar 
                steps={['Personal Info', 'Address', 'Categories', 'Brands', 'Verification']} 
                currentStep={registrationStep} 
              />
              
              <div className="mt-8">
                {renderCurrentStep()}
              </div>
              
              <div className="mt-8 text-center">
                <p className="text-sm text-gray-600">
                  Already have an account?{' '}
                  <button 
                    onClick={() => navigate('/login')}
                    className="font-medium text-orange-600 hover:text-orange-500 transition-colors"
                  >
                    Sign in
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="mt-4 text-center text-white text-sm">
        © {new Date().getFullYear()} ZoneXVendors. All rights reserved.
      </div>
    </div>
  );
};

export default RegisterPage;